import { NotificationDto } from '../dto';
import { NotificationType } from '../enums';

type Severity = 'info' | 'success' | 'warn' | 'danger' | 'secondary';

export class NotificationEntity {
  readonly id: number;
  readonly userId: number;
  readonly type: NotificationType;
  readonly payload: Record<string, unknown>;
  readonly link: string | null;
  readonly readAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;

  constructor(dto: NotificationDto) {
    this.id = dto.id;
    this.userId = dto.userId;
    this.type = dto.type;
    this.payload = dto.payload ?? {};
    this.link = dto.link;
    this.readAt = dto.readAt;
    this.createdAt = dto.createdAt;
    this.updatedAt = dto.updatedAt;
  }

  get isUnread(): boolean {
    return this.readAt === null;
  }

  get titleKey(): string {
    return `notifications.types.${this.type}.title`;
  }

  get messageKey(): string {
    return `notifications.types.${this.type}.message`;
  }

  get translationParams(): Record<string, unknown> {
    return this.payload;
  }

  get iconClass(): string {
    switch (this.type) {
      case NotificationType.ACCESS_REQUEST_RECEIVED:
      case NotificationType.ACCESS_REQUEST_EXPIRING:
        return 'pi pi-key';
      case NotificationType.ACCESS_REQUEST_ACCEPTED:
        return 'pi pi-check-circle';
      case NotificationType.ACCESS_REQUEST_REJECTED:
      case NotificationType.ACCESS_REQUEST_EXPIRED:
      case NotificationType.ACCESS_REVOKED:
        return 'pi pi-times-circle';
      case NotificationType.ADDED_TO_PIPELINE:
      case NotificationType.STATUS_CHANGED:
        return 'pi pi-sitemap';
      case NotificationType.CV_VIEWED:
        return 'pi pi-eye';
      case NotificationType.INTERVIEW_SCHEDULED:
      case NotificationType.INTERVIEW_REMINDER:
        return 'pi pi-calendar';
      case NotificationType.INTERVIEW_CANCELLED:
        return 'pi pi-calendar-times';
      case NotificationType.RECRUITMENT_FINALIZED:
        return 'pi pi-flag';
      case NotificationType.NEW_MESSAGE:
        return 'pi pi-comments';
      case NotificationType.APPLICATION_STAGE_CHANGED:
        return 'pi pi-arrow-right-arrow-left';
      case NotificationType.APPLICATION_OUTCOME_HIRED:
        return 'pi pi-check-circle';
      case NotificationType.APPLICATION_OUTCOME_REJECTED:
        return 'pi pi-info-circle';
      case NotificationType.APPLICATION_AWAITING_CANDIDATE:
        return 'pi pi-bell';
      case NotificationType.APPLICATION_MEETING_SCHEDULED:
        return 'pi pi-calendar';
      case NotificationType.APPLICATION_MEETING_RESCHEDULED:
        return 'pi pi-calendar-clock';
      case NotificationType.APPLICATION_MEETING_CANCELLED:
        return 'pi pi-calendar-times';
      case NotificationType.APPLICATION_MESSAGE_RECEIVED:
        return 'pi pi-envelope';
      default:
        return 'pi pi-bell';
    }
  }

  get severity(): Severity {
    switch (this.type) {
      case NotificationType.ACCESS_REQUEST_ACCEPTED:
      case NotificationType.RECRUITMENT_FINALIZED:
      case NotificationType.APPLICATION_OUTCOME_HIRED:
        return 'success';
      case NotificationType.ACCESS_REQUEST_REJECTED:
      case NotificationType.ACCESS_REQUEST_EXPIRED:
      case NotificationType.ACCESS_REVOKED:
      case NotificationType.INTERVIEW_CANCELLED:
      case NotificationType.APPLICATION_MEETING_CANCELLED:
        return 'danger';
      case NotificationType.ACCESS_REQUEST_EXPIRING:
      case NotificationType.INTERVIEW_REMINDER:
      case NotificationType.APPLICATION_AWAITING_CANDIDATE:
      case NotificationType.APPLICATION_MEETING_RESCHEDULED:
        return 'warn';
      case NotificationType.NEW_MESSAGE:
      case NotificationType.STATUS_CHANGED:
      case NotificationType.ADDED_TO_PIPELINE:
      case NotificationType.CV_VIEWED:
      case NotificationType.APPLICATION_STAGE_CHANGED:
      case NotificationType.APPLICATION_MEETING_SCHEDULED:
      case NotificationType.APPLICATION_MESSAGE_RECEIVED:
        return 'info';
      case NotificationType.APPLICATION_OUTCOME_REJECTED:
        return 'secondary';
      default:
        return 'secondary';
    }
  }

  get formattedDate(): string {
    return new Date(this.createdAt).toLocaleString();
  }

  get relativeTime(): string {
    const now = Date.now();
    const created = new Date(this.createdAt).getTime();
    const diffSec = Math.max(1, Math.floor((now - created) / 1000));

    if (diffSec < 60) return `${diffSec}s`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 30) return `${diffD}d`;
    const diffMo = Math.floor(diffD / 30);
    if (diffMo < 12) return `${diffMo}mo`;
    const diffY = Math.floor(diffMo / 12);
    return `${diffY}y`;
  }
}
